// Shared read-state helpers for Lounge + match-room timelines.

/** MongoDB ObjectId — only these should be persisted to server / AsyncStorage. */
export const isPersistableMessageId = (id) =>
  /^[a-f\d]{24}$/i.test(String(id || ''));

export const idEq = (raw, target) => {
  const v = typeof raw === 'string' ? raw : raw?._id;
  return v && String(v) === String(target);
};

export const isMessageReadByUser = (msg, userId) => {
  if (!msg || !userId) return true;
  return (msg.readBy || []).some((id) => idEq(id, userId));
};

export const isIncomingMessage = (msg, userId) => {
  if (!msg?._id) return false;
  const authorId = msg.userId?._id ?? msg.userId;
  return authorId && !idEq(authorId, userId);
};

export const messageMatchesId = (msg, messageId) => {
  if (!msg || !messageId) return false;
  return idEq(msg._id, messageId) || idEq(msg.tempId, messageId);
};

export const findMessageIndexById = (messages, messageId) => {
  if (!messageId) return -1;
  return messages.findIndex((m) => messageMatchesId(m, messageId));
};

/** Index of the saved read cursor in the chronological list. */
export const findCursorIndex = (messages, lastReadMessageId) =>
  findMessageIndexById(messages, lastReadMessageId);

/** True when the saved cursor is present in the loaded message window. */
export const isCursorInMessages = (messages, cursorId) =>
  findCursorIndex(messages, cursorId) >= 0;

/**
 * First actually-unread incoming message at/after the cursor.
 * If there is no cursor, falls back to first incoming unread overall.
 * Returns -1 if everything is already read.
 */
export const findFirstUnreadAfterCursor = (messages, userId, lastReadMessageId) => {
  const cursorIdx = findCursorIndex(messages, lastReadMessageId);
  const startIdx = cursorIdx >= 0 ? cursorIdx + 1 : 0;
  for (let i = startIdx; i < messages.length; i++) {
    const m = messages[i];
    if (!isIncomingMessage(m, userId)) continue;
    if (!isMessageReadByUser(m, userId)) return i;
  }
  return -1;
};

/** Number of actually-unread incoming messages after the cursor. */
export const countMessagesAfterCursor = (messages, userId, lastReadMessageId) => {
  const firstIdx = findFirstUnreadAfterCursor(messages, userId, lastReadMessageId);
  if (firstIdx < 0) return 0;
  let count = 0;
  for (let i = firstIdx; i < messages.length; i++) {
    const m = messages[i];
    if (isIncomingMessage(m, userId) && !isMessageReadByUser(m, userId)) count += 1;
  }
  return count;
};

export const findFirstUnreadMessageIndex = (messages, userId) => {
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (!isIncomingMessage(m, userId)) continue;
    if (!isMessageReadByUser(m, userId)) return i;
  }
  return -1;
};

/** Pick the chronologically latest message from a list. */
export const latestMessageByTime = (list) => {
  if (!list?.length) return null;
  return list.reduce((best, m) => {
    if (!best) return m;
    return new Date(m.createdAt).getTime() >= new Date(best.createdAt).getTime() ? m : best;
  }, null);
};

export const countIncomingUnread = (messages, userId) =>
  messages.filter((m) => isIncomingMessage(m, userId) && !isMessageReadByUser(m, userId)).length;

export const countReadByOthers = (msg, userId) =>
  (msg.readBy || []).filter((id) => !idEq(id, userId)).length;

/** True when messageId is in the batch (string-safe). */
export const isIdInBatch = (messageId, messageIds) => {
  if (!messageId || !messageIds?.length) return false;
  const target = String(messageId);
  return messageIds.some((id) => id != null && String(id) === target);
};

/**
 * Collect server-persistable ids for incoming messages the user has not read yet.
 * Works for plain text, polls, fixture tags, and reply messages alike.
 */
export const collectMarkReadIds = (messages, userId) => {
  if (!Array.isArray(messages) || !userId) return [];
  const out = [];
  const seen = new Set();
  for (const msg of messages) {
    if (!isIncomingMessage(msg, userId) || isMessageReadByUser(msg, userId)) continue;
    const id = msg._id;
    if (!isPersistableMessageId(id)) continue;
    const key = String(id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
};

/**
 * Furthest visible message the user has effectively read.
 * Includes own messages, already-read incoming, and incoming being marked now.
 */
export const pickReadCursorFromVisible = (visibleMsgs, userId, markingIds = []) => {
  if (!visibleMsgs?.length) return null;
  const marking = new Set((markingIds || []).map(String));
  const candidates = visibleMsgs.filter((m) => {
    if (!isIncomingMessage(m, userId)) return true;
    if (isMessageReadByUser(m, userId)) return true;
    return marking.has(String(m._id));
  });
  const latest = latestMessageByTime(candidates);
  return latest?._id || latest?.tempId || null;
};
